import { DecimalPipe } from '@angular/common';
import { HttpErrorResponse } from '@angular/common/http';
import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  OnInit,
  computed,
  inject,
  signal,
} from '@angular/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { FormControl, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { ActivatedRoute, Router } from '@angular/router';
import { BlockSpinnerDirective, ExtendedNotificationComponent } from '@next/core-lib/components';
import { AgrNumberDirective } from '@next/core-lib/form';
import { TranslatePipe } from '@next/core-lib/i18n';

import { DairyYieldScanSummary } from '../../models/dairy-yield-scan.models';
import { DairyYieldScanActionPlanDraftStore } from '../../services/dairy-yield-scan-action-plan-draft.store';
import { DairyYieldCalculationService } from '../../services/dairy-yield-calculation.service';
import { DairyYieldScanService } from '../../services/dairy-yield-scan.service';
import { DairyYieldScanActionsComponent } from '../dairy-yield-scan-actions/dairy-yield-scan-actions.component';
import { DairyYieldScanCategoryPotentialsComponent } from '../dairy-yield-scan-category-potentials';
import { DairyYieldScanCustomerDetailsComponent } from '../dairy-yield-scan-customer-details/dairy-yield-scan-customer-details.component';
import { DairyYieldScanInsightsComponent } from '../dairy-yield-scan-insights/dairy-yield-scan-insights.component';
import { DairyYieldScanPrintSummaryComponent } from '../dairy-yield-scan-print-summary/dairy-yield-scan-print-summary.component';

const DAYS_PER_YEAR = 365;
const KILOGRAMS_PER_HUNDRED = 100;
type SummaryState =
  | { status: 'loading' }
  | { status: 'loaded'; summary: DairyYieldScanSummary }
  | { status: 'notFound' }
  | { status: 'error' };

@Component({
  selector: 'app-dairy-yield-scan-summary',
  templateUrl: './dairy-yield-scan-summary.component.html',
  styleUrls: ['./dairy-yield-scan-summary.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [DairyYieldScanActionPlanDraftStore],
  imports: [
    AgrNumberDirective,
    BlockSpinnerDirective,
    DairyYieldScanActionsComponent,
    DairyYieldScanCategoryPotentialsComponent,
    DairyYieldScanCustomerDetailsComponent,
    DairyYieldScanInsightsComponent,
    DairyYieldScanPrintSummaryComponent,
    DecimalPipe,
    ExtendedNotificationComponent,
    MatFormFieldModule,
    MatButtonModule,
    MatIconModule,
    MatInputModule,
    ReactiveFormsModule,
    TranslatePipe,
  ],
})
export class DairyYieldScanSummaryComponent implements OnInit {
  readonly errorNotificationType = 'error';
  readonly state = signal<SummaryState>({ status: 'loading' });
  readonly summary = computed(() => {
    const state = this.state();
    return state.status === 'loaded' ? state.summary : null;
  });
  readonly milkPriceControl = new FormControl<number | null>(50, Validators.min(0));
  readonly annualPotentialMilk = computed(() => {
    const summary = this.summary();
    return summary?.dairyCowCount === null || summary?.dairyCowCount === undefined
      ? null
      : summary.baselinePotential * summary.dairyCowCount * DAYS_PER_YEAR;
  });
  readonly annualTargetMilk = computed(() => {
    const summary = this.summary();
    if (
      summary?.dairyCowCount === null ||
      summary?.dairyCowCount === undefined ||
      summary.currentMilkProduction === null ||
      summary.targetMilkProduction === null
    ) {
      return null;
    }

    return (
      Math.max(0, summary.targetMilkProduction - summary.currentMilkProduction) *
      summary.dairyCowCount *
      DAYS_PER_YEAR
    );
  });
  readonly annualPotentialRevenue = computed(() =>
    this.calculateRevenue(this.annualPotentialMilk()),
  );
  readonly annualTargetRevenue = computed(() => this.calculateRevenue(this.annualTargetMilk()));
  readonly currentMilkYield = computed(() => {
    const summary = this.summary();
    if (
      summary?.currentMilkProduction === null ||
      summary?.currentMilkProduction === undefined ||
      summary.currentFatPercentage === null ||
      summary.currentFatPercentage === undefined ||
      summary.currentProteinPercentage === null ||
      summary.currentProteinPercentage === undefined
    ) {
      return null;
    }

    return this.dairyYieldCalculationService.calculateMeetmelk({
      milkProduction: summary.currentMilkProduction,
      fatPercentage: summary.currentFatPercentage,
      proteinPercentage: summary.currentProteinPercentage,
    });
  });
  readonly targetMilkYield = computed(() => {
    const summary = this.summary();
    if (
      summary?.targetMilkProduction === null ||
      summary?.targetMilkProduction === undefined ||
      summary.targetFatPercentage === null ||
      summary.targetFatPercentage === undefined ||
      summary.targetProteinPercentage === null ||
      summary.targetProteinPercentage === undefined
    ) {
      return null;
    }

    return this.dairyYieldCalculationService.calculateMeetmelk({
      milkProduction: summary.targetMilkProduction,
      fatPercentage: summary.targetFatPercentage,
      proteinPercentage: summary.targetProteinPercentage,
    });
  });

  private readonly destroyRef = inject(DestroyRef);
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly dairyYieldCalculationService = inject(DairyYieldCalculationService);
  private readonly dairyYieldScanService = inject(DairyYieldScanService);
  private readonly milkPrice = toSignal(this.milkPriceControl.valueChanges, {
    initialValue: this.milkPriceControl.value,
  });

  ngOnInit() {
    this.loadSummary();
  }

  loadSummary() {
    const scanId = this.route.snapshot.paramMap.get('scanId');
    if (!scanId) {
      this.state.set({ status: 'notFound' });
      return;
    }

    this.state.set({ status: 'loading' });
    this.dairyYieldScanService
      .getScanSummary(scanId)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (summary) => this.state.set({ status: 'loaded', summary }),
        error: (error: unknown) =>
          this.state.set({
            status:
              error instanceof HttpErrorResponse && error.status === 404 ? 'notFound' : 'error',
          }),
      });
  }

  startNewScan() {
    void this.router.navigate(['/']);
  }

  printSummary() {
    const originalTitle = document.title;
    const date = new Date().toLocaleDateString('nl-NL').replaceAll('/', '-');
    document.title = `MelkVermogen-scan_${date}`;

    setTimeout(() => {
      globalThis.print();
      document.title = originalTitle;
    }, 50);
  }

  private calculateRevenue(annualMilk: number | null): number | null {
    const milkPrice = this.milkPrice();
    return annualMilk === null || milkPrice === null || this.milkPriceControl.invalid
      ? null
      : (annualMilk * milkPrice) / KILOGRAMS_PER_HUNDRED;
  }
}
